import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Check, Clock, Building2, MessageSquare, Send, Loader2 } from "lucide-react";

function formatWhen(iv) {
    const iso = iv?.scheduled_at;
    if (!iso) return "TBD";
    if (iv?.scheduled_date_only) {
        const ymd = String(iso).slice(0, 10);
        try {
            const [y, m, d] = ymd.split("-").map(Number);
            const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
            return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
        } catch { return ymd; }
    }
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

function timeAgo(iso) {
    if (!iso) return "";
    try {
        const diff = (Date.now() - new Date(iso).getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    } catch { return ""; }
}

/* ------------------------------------------------------------------
 * Iter141 — One-tap reply + inline chat thread per interview card
 * ------------------------------------------------------------------ */
function ChatBox({ iv, me }) {
    const [open, setOpen] = useState(false);
    const [thread, setThread] = useState([]);
    const [body, setBody] = useState("");
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [autoFocus, setAutoFocus] = useState(false);
    const openInputRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/seeker/interviews/${iv.id}/messages`);
            setThread(data?.messages || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);
    useEffect(() => {
        if (open && autoFocus && openInputRef.current) {
            openInputRef.current.focus();
            setAutoFocus(false);
        }
    }, [open, autoFocus]);

    const send = async () => {
        const text = body.trim();
        if (!text) return;
        setSending(true);
        try {
            await api.post(`/seeker/interviews/${iv.id}/message`, { body: text });
            setBody("");
            toast.success("Reply sent to the recruiter");
            // Optimistic refresh
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSending(false); }
    };

    return (
        <div className="col-span-12 border-t border-border mt-1 pt-3" data-testid={`seeker-iv-chat-${iv.id}`}>
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 hover:text-ikb inline-flex items-center gap-1.5"
                    data-testid={`seeker-iv-chat-toggle-${iv.id}`}
                >
                    <MessageSquare className="w-3 h-3" /> {open ? "Hide chat" : "Reply to recruiter"}
                </button>
                {!open && (
                    <div className="flex items-center gap-2 w-full max-w-md ml-4">
                        <input
                            type="text"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            onFocus={() => { setAutoFocus(true); setOpen(true); }}
                            placeholder="Quick reply…"
                            className="flex-1 h-8 px-2 border border-border text-xs"
                            data-testid={`seeker-iv-chat-input-${iv.id}`}
                        />
                        <button
                            type="button"
                            onClick={send}
                            disabled={!body.trim() || sending}
                            className="h-8 w-8 inline-flex items-center justify-center bg-ikb text-white hover:bg-[#001f7a] disabled:opacity-40"
                            data-testid={`seeker-iv-chat-send-${iv.id}`}
                        >
                            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        </button>
                    </div>
                )}
            </div>
            {open && (
                <div className="mt-3 space-y-2" data-testid={`seeker-iv-chat-panel-${iv.id}`}>
                    {loading ? (
                        <div className="text-xs text-muted-foreground font-mono">Loading…</div>
                    ) : thread.length === 0 ? (
                        <div className="text-xs text-muted-foreground font-mono">No messages yet. Send the first reply below.</div>
                    ) : (
                        <div className="max-h-56 overflow-y-auto border border-border divide-y divide-border bg-secondary/40">
                            {thread.map((m) => {
                                const mine = m.sender_role === "seeker" && (!me || m.sender_id === me.id);
                                return (
                                    <div key={m.id} className={`p-2 text-xs ${mine ? "bg-blue-50/50" : "bg-white"}`}>
                                        <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground mb-0.5">
                                            <span className="font-semibold text-foreground/80">{mine ? "You" : (m.sender_name || "Recruiter")}</span>
                                            <span>·</span>
                                            <span>{timeAgo(m.created_at)}</span>
                                        </div>
                                        <div className="whitespace-pre-wrap break-words">{m.body}</div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <input
                            ref={openInputRef}
                            type="text"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                            placeholder="Type a reply… (Enter to send)"
                            className="flex-1 h-9 px-2 border border-border text-sm"
                            data-testid={`seeker-iv-chat-input-open-${iv.id}`}
                        />
                        <button
                            type="button"
                            onClick={send}
                            disabled={!body.trim() || sending}
                            className="h-9 px-3 bg-ikb text-white text-xs font-mono uppercase tracking-widest hover:bg-[#001f7a] disabled:opacity-40 inline-flex items-center gap-1"
                            data-testid={`seeker-iv-chat-send-open-${iv.id}`}
                        >
                            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function SeekerInterviews() {
    const [items, setItems] = useState([]);
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rescheduleFor, setRescheduleFor] = useState(null);
    const [proposedAt, setProposedAt] = useState("");

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get("/seeker/interviews"); setItems(data || []); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => {
        load();
        (async () => {
            try { const { data } = await api.get("/seeker/me"); setMe(data); } catch { /* ignore */ }
        })();
    }, []);

    const accept = async (iid) => {
        try {
            await api.post(`/seeker/interviews/${iid}/reschedule`, { accept: true });
            toast.success("You've accepted this interview");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const propose = async () => {
        if (!proposedAt) { toast.error("Pick a new date & time"); return; }
        try {
            await api.post(`/seeker/interviews/${rescheduleFor}/reschedule`, {
                proposed_at: new Date(proposedAt).toISOString(),
            });
            toast.success("New time proposed. The recruiter will confirm.");
            setRescheduleFor(null); setProposedAt("");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (loading) {
        return <div className="p-6 text-xs text-muted-foreground font-mono">Loading…</div>;
    }

    return (
        <div className="max-w-5xl mx-auto p-3 sm:p-6 lg:p-8 space-y-8" data-testid="seeker-interviews-page">
            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-ikb">Scheduled</div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">My interviews</h1>
                <p className="text-muted-foreground mt-2">Accept the proposed time or suggest another — the hiring team is notified instantly.</p>
            </div>

            {items.length === 0 ? (
                <div className="border border-dashed border-border p-16 text-center bg-white">
                    <h3 className="font-display font-bold text-xl">No interviews scheduled</h3>
                    <p className="text-sm text-muted-foreground mt-2">You'll see every round here as recruiters schedule you.</p>
                </div>
            ) : (
                <div className="border border-border bg-white divide-y divide-border">
                    {items.map((iv) => {
                        const accepted = !!iv.candidate_accepted_at;
                        const proposed = iv.candidate_proposed_at;
                        const closed = ["Cancelled", "Completed", "No-show"].includes(iv.status);
                        return (
                            <div key={iv.id} data-testid={`seeker-iv-${iv.id}`} className="p-5 grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-5">
                                    <div className="font-display font-bold text-base">{iv.round_name || "Interview"}</div>
                                    <div className="flex items-center gap-3 text-xs font-mono text-muted-foreground mt-1 flex-wrap">
                                        <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {iv.company?.name || "Company"}</span>
                                        <span>·</span>
                                        <span>{iv.job?.title || "Role"}</span>
                                    </div>
                                </div>
                                <div className="col-span-12 md:col-span-4 text-xs font-mono text-muted-foreground">
                                    <div className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {formatWhen(iv)}</div>
                                    <div className="inline-flex items-center gap-1.5 ml-3"><Clock className="w-3 h-3" /> {iv.duration_minutes || 30} min · {iv.mode || "Video"}</div>
                                    {proposed && (
                                        <div className="mt-1 text-amber-800">↻ You proposed: {formatWhen({ scheduled_at: proposed, scheduled_date_only: false })}</div>
                                    )}
                                </div>
                                <div className="col-span-12 md:col-span-3 flex items-center md:justify-end gap-2 flex-wrap">
                                    {closed ? (
                                        <span className="text-[10px] font-mono uppercase tracking-widest border border-border bg-secondary text-foreground/70 px-2 py-1">
                                            {iv.status}
                                        </span>
                                    ) : (
                                        <>
                                            {!accepted && (
                                                <button type="button" onClick={() => accept(iv.id)} className="text-[10px] font-mono uppercase tracking-widest border border-ikb text-ikb px-2 py-1 hover:bg-ikb hover:text-white inline-flex items-center gap-1" data-testid={`seeker-iv-accept-${iv.id}`}>
                                                    <Check className="w-3 h-3" /> {proposed ? "Accept original" : "Accept"}
                                                </button>
                                            )}
                                            <button type="button" onClick={() => { setRescheduleFor(iv.id); setProposedAt(""); }} className="text-[10px] font-mono uppercase tracking-widest border border-border hover:border-ikb hover:text-ikb px-2 py-1" data-testid={`seeker-iv-reschedule-${iv.id}`}>
                                                {proposed ? "Change proposal" : "Reschedule"}
                                            </button>
                                            {accepted && (
                                                <span className="text-[10px] font-mono uppercase tracking-widest border border-emerald-300 bg-emerald-50 text-emerald-700 px-2 py-1 inline-flex items-center gap-1">
                                                    <Check className="w-3 h-3" /> Accepted
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                                {rescheduleFor === iv.id && (
                                    <div className="col-span-12 border-t border-border pt-3 mt-1 flex items-center gap-2 flex-wrap" data-testid={`seeker-iv-reschedule-form-${iv.id}`}>
                                        <span className="text-[11px] font-mono text-foreground/70">Propose new time:</span>
                                        <input type="datetime-local" value={proposedAt} onChange={(e) => setProposedAt(e.target.value)} className="h-9 px-2 border border-border text-sm" data-testid={`seeker-iv-reschedule-input-${iv.id}`} />
                                        <button type="button" onClick={propose} className="text-[11px] font-mono uppercase tracking-widest bg-ikb text-white px-3 py-1.5 hover:bg-[#001f7a]" data-testid={`seeker-iv-reschedule-submit-${iv.id}`}>Send</button>
                                        <button type="button" onClick={() => { setRescheduleFor(null); setProposedAt(""); }} className="text-[11px] font-mono uppercase tracking-widest border border-border px-3 py-1.5 hover:bg-secondary">Cancel</button>
                                    </div>
                                )}
                                {!closed && <ChatBox iv={iv} me={me} />}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
